import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { FeedItem } from '../../types';
import ReactionBar from '../shared/ReactionBar';
import CommentsModal from '../shared/CommentsModal';
import './FeedCard.css';

interface VoiceNoteCardProps {
  item: FeedItem;
  currentUserId: number;
  onDelete?: (postId: number) => void;
}

const VoiceNoteCard: React.FC<VoiceNoteCardProps> = ({ item, currentUserId, onDelete }) => {
  const { t } = useLanguage();
  const [showComments, setShowComments] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const isOwner = item.user_id === currentUserId;

  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);

  const timeAgo = (timestamp: string) => {
    const diff = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (diff < 60) return t('feed.timeAgo.now');
    if (diff < 3600) return t('feed.timeAgo.minutes').replace('{{count}}', String(Math.floor(diff / 60)));
    if (diff < 86400) return t('feed.timeAgo.hours').replace('{{count}}', String(Math.floor(diff / 3600)));
    return t('feed.timeAgo.days').replace('{{count}}', String(Math.floor(diff / 86400)));
  };

  const formatDuration = (s?: number) => {
    if (!s) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (!item.audio_url) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(item.audio_url);
      audioRef.current.ontimeupdate = () => {
        if (audioRef.current) {
          setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
        }
      };
      audioRef.current.onended = () => { setPlaying(false); setProgress(0); };
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  return (
    <div className="feed-card">
      <div className="feed-card-header">
        {item.user_profile_picture ? (
          <img src={item.user_profile_picture} alt="" className="feed-card-avatar" />
        ) : (
          <div className="feed-card-avatar-placeholder">
            {item.user_first_name?.[0] || '?'}
          </div>
        )}
        <div className="feed-card-user-info">
          <div className="feed-card-user-name">{item.user_first_name} {item.user_last_name}</div>
          <div className="feed-card-meta">
            <span>{timeAgo(item.timestamp)}</span>
            <span>·</span>
            <span>🎙 {t('feed.voiceNote')}</span>
          </div>
        </div>
        {isOwner && (
          <div className="feed-card-menu-wrapper" ref={menuRef}>
            <button className="feed-card-menu-btn" onClick={() => setShowMenu(v => !v)}>&#8943;</button>
            {showMenu && (
              <div className="feed-card-menu">
                <button className="feed-card-menu-item feed-card-menu-item--danger" onClick={() => {
                  setShowMenu(false);
                  onDelete?.(item.id);
                }}>
                  {t('feed.menu.delete')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="feed-card-voice-note">
        <div className="voice-note-player">
          <button className="voice-note-play-btn" onClick={togglePlay}>
            {playing ? '⏸' : '▶'}
          </button>
          <div className="voice-note-progress">
            <div className="voice-note-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="voice-note-duration">{formatDuration(item.duration_s)}</span>
        </div>
      </div>

      <div className="feed-card-reactions">
        <ReactionBar
          targetType="feed_post"
          targetId={item.id}
          clubupCount={item.clubup_count}
          assCount={item.ass_count}
          hasClubup={item.has_clubup}
          hasAss={item.has_ass}
          commentCount={item.comment_count}
          recentReactors={item.recent_reactors}
          aiComment={item.ai_comment}
          onCommentClick={() => setShowComments(true)}
        />
      </div>

      {showComments && (
        <CommentsModal
          targetType="feed_post"
          targetId={item.id}
          currentUserId={currentUserId}
          onClose={() => setShowComments(false)}
        />
      )}
    </div>
  );
};

export default VoiceNoteCard;
